import {Button} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useMemo} from 'react';

import {formatQuarterCloseLabel, parseApiDate} from './scorecardData';

/**
 * "Quarter Close" dropdown that jumps to an end-of-quarter snapshot. The trigger
 * shows the selected quarter ("Q3 2026"), or "Select" when the active snapshot
 * is not an end-of-quarter snapshot.
 */
export const ScorecardQuarterCloseSelector = ({
  endOfQuarterSnapshots,
  selectedSnapshotDt,
  onSelect,
}: {
  endOfQuarterSnapshots: string[];
  selectedSnapshotDt: string;
  onSelect: (snapshotDt: string) => void;
}) => {
  const options = useMemo(
    () =>
      endOfQuarterSnapshots
        .map((snapshotDt) => {
          const date = parseApiDate(snapshotDt);
          return date ? {snapshotDt, label: formatQuarterCloseLabel(date)} : null;
        })
        .filter((option): option is {snapshotDt: string; label: string} => Boolean(option))
        .sort((a, b) => a.snapshotDt.localeCompare(b.snapshotDt)),
    [endOfQuarterSnapshots]
  );
  const selectedOption = options.find((option) => option.snapshotDt === selectedSnapshotDt);

  return (
    <Split gap="12" alignY="center">
      <Split.Item>
        <Text color="faint">{t('scorecard_quarter_close')}</Text>
      </Split.Item>
      <Split.Item>
        <Menu.Wrapper>
          {({getContentProps, getTriggerProps}) => (
            <>
              <Button {...getTriggerProps()} variant="opacity" withDropdownIcon>
                {selectedOption?.label ?? t('scorecard_quarter_close_select')}
              </Button>
              <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 200}}>
                <Menu.Segment>
                  {options.map((option) => (
                    <Menu.ActionItem
                      key={option.snapshotDt}
                      onClick={() => onSelect(option.snapshotDt)}
                      withRightAccessory={
                        option.snapshotDt === selectedSnapshotDt ? (
                          <UIIcon src={CheckmarkLine} size="small" />
                        ) : undefined
                      }
                    >
                      {option.label}
                    </Menu.ActionItem>
                  ))}
                </Menu.Segment>
              </Menu.Content>
            </>
          )}
        </Menu.Wrapper>
      </Split.Item>
    </Split>
  );
};
